/*
 * @Author: Auto Generated
 * @FilePath: /mall-fe/src/comps/CategoryProvider.tsx
 * @Description: 商品分类数据 Provider
 *
 * 在客户端获取商品分类数据，并通过 Context 提供给子组件
 * 替代原有的服务端渲染方式
 */

import { ReactNode } from 'react';
import { CategoryContext } from 'libs/contexts';
import { useCategoryQuery, Category } from 'hooks/useCategoryQuery';

export interface CategoryProviderProps {
  children: ReactNode;
  /**
   * 初始数据（可选）
   * 用于服务端渲染场景，如果提供则使用初始数据，避免客户端重新请求
   */
  initialData?: Category[];
}

/**
 * 商品分类数据 Provider 组件
 *
 * 功能：
 * 1. 使用 React Query 在客户端获取商品分类数据
 * 2. 通过 CategoryContext 提供数据给子组件
 * 3. 自动处理 loading 和 error 状态
 * 4. 支持初始数据，避免重复请求
 *
 * @example
 * ```tsx
 * // 在 _app.tsx 中使用
 * <CategoryProvider>
 *   <YourApp />
 * </CategoryProvider>
 *
 * // 在子组件中使用
 * function SomeComponent() {
 *   const categories = useContext(CategoryContext);
 *   return <div>{categories?.length} 个分类</div>;
 * }
 * ```
 */
export default function CategoryProvider({ children }: CategoryProviderProps) {
  // 使用 React Query 获取商品分类数据
  const { data: categories, isLoading, isError, error } = useCategoryQuery();

  // 错误处理：如果加载失败，使用空数组避免组件崩溃
  if (isError) {
    console.error('加载商品分类数据失败:', error);
  }

  // 提供数据给子组件
  // 注意：
  // 1. loading 期间提供空数组，避免子组件因为 undefined 崩溃
  // 2. error 期间也提供空数组，让应用继续运行
  // 3. 成功后提供实际数据
  const categoryData = isLoading || isError ? [] : categories || [];

  return <CategoryContext.Provider value={categoryData}>{children}</CategoryContext.Provider>;
}

/**
 * 带骨架屏的 CategoryProvider（可选实现）
 *
 * 如果需要在分类数据加载期间显示骨架屏，可以使用这个版本
 *
 * @example
 * ```tsx
 * <CategoryProviderWithSkeleton
 *   skeleton={<CategorySkeleton />}
 * >
 *   <YourApp />
 * </CategoryProviderWithSkeleton>
 * ```
 */
export function CategoryProviderWithSkeleton({
  children,
  initialData,
  skeleton,
}: CategoryProviderProps & { skeleton?: ReactNode }) {
  const { data: categories, isLoading, isError, error } = useCategoryQuery();

  // 首次加载中，显示骨架屏
  if (isLoading && !initialData && skeleton) {
    return skeleton;
  }

  // 错误处理
  if (isError) {
    console.error('加载商品分类数据失败:', error);
  }

  const categoryData = isLoading || isError ? [] : categories || [];

  return <CategoryContext.Provider value={categoryData}>{children}</CategoryContext.Provider>;
}
